"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.openAIAdapter = void 0;
exports.isOpenAIResponsesRequest = isOpenAIResponsesRequest;
const helpers_1 = require("../helpers");
const zod_1 = require("zod");
/**
 * Detection schemas for OpenAI formats
 * These are permissive - only validate structural markers, not full API contracts
 */
// INPUT SCHEMAS (requests)
const OpenAIInputChatCompletionsSchema = zod_1.z.looseObject({
    messages: zod_1.z.array(zod_1.z.any()),
    tools: zod_1.z.array(zod_1.z.any()).optional(),
});
const OpenAIInputMessagesSchema = zod_1.z
    .array(zod_1.z.looseObject({
    role: zod_1.z.enum(["system", "user", "assistant", "tool", "function"]),
}))
    .refine((data) => {
    // Reject if any message has top-level parts (Microsoft Agent/Gemini format)
    // OpenAI uses parts inside content, not at message level
    return !data.some((msg) => typeof msg === "object" &&
        msg !== null &&
        "parts" in msg &&
        Array.isArray(msg.parts));
}, { message: "Messages with top-level parts are not OpenAI format" });
// Role-less Responses input item types. normalizeMessage converts the common
// conversation items and preserves built-in tool items as JSON passthrough.
// Reference: https://platform.openai.com/docs/api-reference/responses/create
const RESPONSES_ITEM_TYPES = new Set([
    "reasoning",
    "function_call",
    "tool_call",
    "function_call_output",
    "file_search_call",
    "computer_call",
    "computer_call_output",
    "web_search_call",
    "code_interpreter_call",
    "image_generation_call",
    "local_shell_call",
    "local_shell_call_output",
    "shell_call",
    "shell_call_output",
    "apply_patch_call",
    "apply_patch_call_output",
    "mcp_list_tools",
    "mcp_approval_request",
    "mcp_approval_response",
    "mcp_call",
    "custom_tool_call",
    "custom_tool_call_output",
    "tool_search_call",
    "tool_search_output",
    "compaction",
    "item_reference",
]);
/**
 * Check that a Responses API request `input` array holds role-based messages
 * or typed Responses items normalizeMessage converts or preserves. Rejects string items
 * (embeddings-style input) and messages with top-level parts (Microsoft
 * Agent/Gemini format).
 */
function isResponsesApiInputArray(value) {
    return (Array.isArray(value) &&
        value.length > 0 &&
        value.every((item) => {
            if (!item || typeof item !== "object")
                return false;
            const msg = item;
            if (Array.isArray(msg.parts))
                return false;
            if (typeof msg.role === "string")
                return true;
            return typeof msg.type === "string" && RESPONSES_ITEM_TYPES.has(msg.type);
        }));
}
// Bare {input: "..."} is too generic to claim; a string input needs a
// distinctive Responses API request sibling.
const RESPONSES_REQUEST_SIBLINGS = [
    "tools",
    "tool_choice",
    "parallel_tool_calls",
    "instructions",
];
/**
 * Responses API request: {input: [...] | "...", tools?, instructions?, ...}
 * Shared by detect() and preprocessData so detection cannot drift from what
 * preprocessing consumes. Also used by extractAdditionalInput to hide `input`
 * from additional input only when it holds the conversation.
 */
function isOpenAIResponsesRequest(data) {
    if (!data || typeof data !== "object" || Array.isArray(data))
        return false;
    if ("messages" in data || "output" in data)
        return false;
    const obj = data;
    if (isResponsesApiInputArray(obj.input))
        return true;
    return (typeof obj.input === "string" &&
        obj.input.length > 0 &&
        RESPONSES_REQUEST_SIBLINGS.some((key) => key in obj));
}
const OpenAIInputResponsesSchema = zod_1.z.custom(isOpenAIResponsesRequest);
// OUTPUT SCHEMAS (responses)
const OpenAIOutputResponsesSchema = zod_1.z.looseObject({
    output: zod_1.z.array(zod_1.z.any()),
    tools: zod_1.z.array(zod_1.z.any()).optional(),
});
const OpenAIOutputChoicesSchema = zod_1.z.looseObject({
    model: zod_1.z.string(),
    choices: zod_1.z.array(zod_1.z.any()),
});
const OpenAIOutputSingleMessageSchema = zod_1.z.looseObject({
    role: zod_1.z.string(),
    tool_calls: zod_1.z.array(zod_1.z.looseObject({
        type: zod_1.z.string(),
        function: zod_1.z.looseObject({
            name: zod_1.z.string(),
        }),
    })),
});
/**
 * Extract thinking content from OpenAI Responses API reasoning item
 * Format: { type: "reasoning", content: [...], summary: [...] }
 */
function extractReasoningContent(item) {
    if (item.type !== "reasoning")
        return null;
    const contentArr = Array.isArray(item.content) ? item.content : [];
    const summaryArr = Array.isArray(item.summary) ? item.summary : [];
    // Extract text from content array (may contain {type: "text", text: "..."})
    const contentText = contentArr
        .map((c) => {
        if (typeof c === "string")
            return c;
        if (c && typeof c === "object" && "text" in c) {
            return c.text;
        }
        return "";
    })
        .filter(Boolean)
        .join("\n");
    // Extract summary text
    const summaryText = summaryArr
        .map((s) => {
        if (typeof s === "string")
            return s;
        if (s && typeof s === "object" && "text" in s) {
            return s.text;
        }
        return "";
    })
        .filter(Boolean)
        .join("\n");
    if (!contentText && !summaryText) {
        return typeof item.encrypted_content === "string" &&
            item.encrypted_content !== ""
            ? {
                redacted_thinking: [
                    {
                        type: "redacted_thinking",
                        data: item.encrypted_content,
                    },
                ],
            }
            : null;
    }
    return {
        thinking: [
            {
                type: "thinking",
                content: contentText || summaryText,
                ...(summaryText && contentText ? { summary: summaryText } : {}),
            },
        ],
    };
}
function normalizeMessage(msg) {
    if (!msg || typeof msg !== "object")
        return {};
    // we want to do type-based conversions BEFORE removeNullFields
    // because removeNullFields moves unrecognized fields to json passthrough
    let working = msg;
    // Handle OpenAI Responses API reasoning item
    // Format: { type: "reasoning", content: [...], summary: [...] }
    const reasoningContent = extractReasoningContent(working);
    if (reasoningContent) {
        return {
            role: "assistant",
            content: "",
            ...reasoningContent,
        };
    }
    // Convert direct function call message to tool_calls array format
    // Format: { type: "function_call", name: "...", arguments: {...}, call_id: "..." }
    // Convert to: { role: "assistant", tool_calls: [{ id, name, arguments, type }] }
    if ((working.type === "function_call" || working.type === "tool_call") &&
        working.name &&
        typeof working.name === "string") {
        const toolCall = {
            id: working.call_id || working.id || "",
            name: working.name,
            arguments: typeof working.arguments === "string"
                ? working.arguments
                : JSON.stringify(working.arguments ?? {}),
            type: "function",
        };
        const { type: _type, name: _name, arguments: _args, call_id: _call_id, id: _id, status: _status, ...rest } = working;
        working = {
            ...rest,
            role: rest.role || "assistant",
            tool_calls: [toolCall],
        };
    }
    // Convert function_call_output to standard tool message format
    // Format: { type: "function_call_output", call_id: "...", output: "..." }
    // Convert to: { role: "tool", tool_call_id: "...", content: "..." }
    if (working.type === "function_call_output") {
        const content = typeof working.output === "string"
            ? working.output
            : JSON.stringify(working.output ?? "");
        const { type: _type, call_id, output: _output, ...rest } = working;
        working = {
            ...rest,
            role: "tool",
            tool_call_id: call_id,
            content,
        };
    }
    // Now apply removeNullFields after conversions
    let normalized = (0, helpers_1.removeNullFields)(working);
    // Extract text from OpenAI Agents output_text format
    // Format: content: [{type: "output_text", text: "..."}]
    if (normalized.content &&
        Array.isArray(normalized.content) &&
        normalized.content.length > 0) {
        const firstItem = normalized.content[0];
        if (firstItem &&
            typeof firstItem === "object" &&
            firstItem.type === "output_text" &&
            typeof firstItem.text === "string") {
            // Extract just the text for simple output
            normalized.content = firstItem.text;
        }
    }
    // Normalize camelCase toolCalls to snake_case tool_calls (VAPI framework does this, otherwise it matches OpenAI)
    if ("toolCalls" in normalized && Array.isArray(normalized.toolCalls)) {
        normalized.tool_calls = normalized.toolCalls;
        delete normalized.toolCalls;
    }
    // Flatten OpenAI nested tool_calls format: function.name → name, function.arguments → arguments
    // Format: { id, type: "function", function: { name, arguments } }
    // Convert to: { id, name, arguments, type }
    if (normalized.tool_calls && Array.isArray(normalized.tool_calls)) {
        const sanitizedToolCalls = normalized.tool_calls.filter((tc) => Boolean(tc) && typeof tc === "object");
        normalized.tool_calls = sanitizedToolCalls.map((tc) => {
            if (tc.function && typeof tc.function === "object") {
                const func = tc.function;
                return {
                    id: tc.id,
                    name: func.name,
                    arguments: typeof func.arguments === "string"
                        ? func.arguments
                        : JSON.stringify(func.arguments ?? {}),
                    type: tc.type || "function",
                    index: tc.index,
                };
            }
            // Already flat format, just ensure arguments is stringified
            return {
                ...tc,
                arguments: typeof tc.arguments === "string"
                    ? tc.arguments
                    : JSON.stringify(tc.arguments ?? {}),
            };
        });
    }
    // For tool messages with rich object content, spread into message
    // so it goes to json passthrough field and renders as PrettyJsonView.
    // Rich = nested structure OR 3+ keys. Simple <=2 scalar keys.
    if (normalized.role === "tool" &&
        typeof normalized.content === "object" &&
        normalized.content !== null &&
        !Array.isArray(normalized.content)) {
        if ((0, helpers_1.isRichToolResult)(normalized.content)) {
            // Rich object: spread for table rendering
            const { content, ...rest } = normalized;
            return { ...rest, ...content };
        }
        // Simple object: stringify for text rendering
        normalized.content = (0, helpers_1.stringifyToolResultContent)(normalized.content);
    }
    return normalized;
}
/**
 * Flatten tool definition from nested or flat format to standard format
 * Handles both Chat Completions {type, function: {name, ...}} and flat {name, ...}
 */
function flattenToolDefinition(tool) {
    if (typeof tool !== "object" || !tool)
        return {};
    const t = tool;
    // Handle nested {type, function: {name, ...}} or flat {name, ...}
    const toolFunc = t.function ?? t;
    const toolDef = {
        name: toolFunc.name,
        description: toolFunc.description ?? "",
    };
    if (toolFunc.parameters != null)
        toolDef.parameters = toolFunc.parameters;
    return toolDef;
}
function normalizeMessagesWithTools(messages, tools) {
    const normalizedTools = tools.map(flattenToolDefinition);
    return messages.map((message) => ({
        ...normalizeMessage(message),
        tools: normalizedTools,
    }));
}
function preprocessData(data) {
    if (!data)
        return data;
    // OpenAI Chat Completions API: {tools, messages} OR Responses API: {tools, output}
    // References:
    // - https://platform.openai.com/docs/api-reference/chat/create
    // - https://platform.openai.com/docs/api-reference/responses
    if (typeof data === "object" &&
        !Array.isArray(data) &&
        "tools" in data &&
        (("messages" in data && !("output" in data)) || "output" in data)) {
        const obj = data;
        const messagesArray = (obj.messages ?? obj.output);
        if (Array.isArray(messagesArray) && Array.isArray(obj.tools)) {
            // Attach tools to all messages
            return normalizeMessagesWithTools(messagesArray, obj.tools);
        }
    }
    // Responses API without tools: {output: [...]}
    if (typeof data === "object" &&
        !Array.isArray(data) &&
        "output" in data &&
        !("messages" in data) &&
        !("tools" in data)) {
        const obj = data;
        if (Array.isArray(obj.output)) {
            return obj.output.map(normalizeMessage);
        }
    }
    // Responses API request: {input: [...] | "...", tools?, instructions?, ...}
    // Reference: https://platform.openai.com/docs/api-reference/responses/create
    if (isOpenAIResponsesRequest(data)) {
        const items = typeof data.input === "string"
            ? [{ role: "user", content: data.input }]
            : data.input;
        const messages = typeof data.instructions === "string" && data.instructions !== ""
            ? [{ role: "system", content: data.instructions }].concat(items)
            : items;
        if (Array.isArray(data.tools)) {
            // Attach tools to all messages
            return normalizeMessagesWithTools(messages, data.tools);
        }
        return messages.map(normalizeMessage);
    }
    // Chat Completions response: {choices: [{message: {...}}]}
    if (typeof data === "object" && !Array.isArray(data) && "choices" in data) {
        const obj = data;
        if (Array.isArray(obj.choices) && obj.choices.length > 0) {
            const firstChoice = obj.choices[0];
            if (firstChoice &&
                typeof firstChoice === "object" &&
                "message" in firstChoice) {
                return normalizeMessage(firstChoice.message);
            }
        }
    }
    if (typeof data === "object" &&
        !Array.isArray(data) &&
        ["function_call", "tool_call", "function_call_output"].includes(String(data.type))) {
        return normalizeMessage(data);
    }
    // Array of messages
    if (Array.isArray(data)) {
        return data.map(normalizeMessage);
    }
    // Object with messages key
    if (typeof data === "object" && "messages" in data) {
        const obj = data;
        return {
            ...obj,
            messages: Array.isArray(obj.messages)
                ? obj.messages.map(normalizeMessage)
                : obj.messages,
        };
    }
    // Single message
    if (typeof data === "object" && "role" in data) {
        return normalizeMessage(data);
    }
    return data;
}
exports.openAIAdapter = {
    id: "openai",
    detect(ctx) {
        const meta = (0, helpers_1.parseMetadata)(ctx.metadata);
        // REJECTIONS: Explicit rejection of LangGraph/LangChain/Semantic/Pydantic Kernel formats
        if (meta && typeof meta === "object") {
            // LangGraph
            if ("langgraph_step" in meta ||
                "langgraph_node" in meta ||
                "langgraph_path" in meta ||
                meta.framework === "langgraph" ||
                (Array.isArray(meta.tags) && meta.tags.includes("langgraph"))) {
                return false;
            }
            // Semantic Kernel (scope.name starts with Microsoft.SemanticKernel)
            if ("scope" in meta &&
                meta.scope !== null &&
                typeof meta.scope === "object") {
                const scope = meta.scope;
                if (typeof scope.name === "string" &&
                    scope.name.startsWith("Microsoft.SemanticKernel")) {
                    return false;
                }
                if (scope.name === "agent_framework" ||
                    (typeof scope.name === "string" &&
                        scope.name.includes("Microsoft.Extensions.AI"))) {
                    return false;
                }
            }
            const providerName = (0, helpers_1.getNestedProperty)(meta, "attributes", "gen_ai.provider.name");
            if (providerName === "microsoft.agent_framework")
                return false;
            // Pydantic ai
            if ("scope" in meta &&
                meta.scope !== null &&
                typeof meta.scope === "object") {
                const scope = meta.scope;
                if (scope.name === "pydantic-ai") {
                    return false;
                }
            }
            // LangChain (type without role)
            if (ctx.metadata &&
                typeof ctx.metadata === "object" &&
                "messages" in ctx.metadata) {
                const messages = ctx.metadata.messages;
                if (Array.isArray(messages)) {
                    const hasLangChainType = messages.some((msg) => {
                        // Null guard: ensure msg exists and is an object before accessing properties
                        if (!msg || typeof msg !== "object") {
                            return false;
                        }
                        const message = msg;
                        return (message.type &&
                            typeof message.type === "string" &&
                            ["human", "ai", "tool", "system"].includes(message.type) &&
                            !("role" in message));
                    });
                    if (hasLangChainType)
                        return false;
                }
            }
        }
        // HINTS: Fast checks for explicit OpenAI indicators
        if (ctx.framework === "openai")
            return true;
        if (ctx.observationName?.toLowerCase().includes("openai"))
            return true;
        if (meta?.ls_provider === "openai")
            return true;
        // Metadata attributes check
        if (meta && typeof meta === "object" && "attributes" in meta) {
            const attributes = meta.attributes;
            if (attributes &&
                typeof attributes === "object" &&
                attributes["llm.system"] === "openai") {
                return true;
            }
        }
        // STRUCTURAL: Schema-based detection on metadata
        if (OpenAIInputChatCompletionsSchema.safeParse(ctx.metadata).success)
            return true;
        if (OpenAIInputMessagesSchema.safeParse(ctx.metadata).success)
            return true;
        if (OpenAIInputResponsesSchema.safeParse(ctx.metadata).success)
            return true;
        if (OpenAIOutputResponsesSchema.safeParse(ctx.metadata).success)
            return true;
        if (OpenAIOutputChoicesSchema.safeParse(ctx.metadata).success)
            return true;
        if (OpenAIOutputSingleMessageSchema.safeParse(ctx.metadata).success)
            return true;
        // finally, test on data if available. we might've done this already if we passed
        // data into metadata. we only do this last due to performance concerns.
        if (OpenAIInputChatCompletionsSchema.safeParse(ctx.data).success)
            return true;
        if (OpenAIInputMessagesSchema.safeParse(ctx.data).success)
            return true;
        if (OpenAIInputResponsesSchema.safeParse(ctx.data).success)
            return true;
        if (OpenAIOutputResponsesSchema.safeParse(ctx.data).success)
            return true;
        if (OpenAIOutputChoicesSchema.safeParse(ctx.data).success)
            return true;
        if (OpenAIOutputSingleMessageSchema.safeParse(ctx.data).success)
            return true;
        return false;
    },
    preprocess(data, _kind, _ctx) {
        return preprocessData(data);
    },
    getConsumedInputKeys(data) {
        if (!isOpenAIResponsesRequest(data))
            return [];
        return typeof data.instructions === "string" && data.instructions !== ""
            ? ["input", "instructions"]
            : ["input"];
    },
};
